import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect, Textarea } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { LABOR_VITALS_DATE_TIME_LINK_ID } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/constants/labor-vitals-link-ids";
import type { LaborVitalsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/types/labor-vitals-form";
import {
  getTemperatureUnitSelectValue,
  getUnitSystemFromTemperatureUnitSelectValue,
  valueSetContainsToNativeSelectOptions,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import {
  VitalsFormStack,
  VitalsHeartRateInput,
  VitalsLabeledSection,
  VitalsNumberInput,
  VitalsPairedFields,
} from "@/encounters/components/VitalSigns/components/VitalsFormLayout";
import { VitalsUnitRightSection } from "@/encounters/components/VitalSigns/components/VitalsUnitRightSection";
import { VitalsUnitsToggle } from "@/encounters/components/VitalSigns/components/VitalsUnitsToggle";
import {
  convertTemperatureValue,
  getTemperatureUnitSuffix,
  getVitalsUnitFields,
  VitalsUnitSystem,
} from "@/encounters/components/VitalSigns/utils/vitals-unit-system";
import { LOINCValueSets } from "@/fhir/enums/loinc";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";

interface LaborVitalsFormFieldsProps {
  form: UseFormReturnType<LaborVitalsFlowsheetFormValues>;
  clientId: string;
  mode?: "quick-add" | "edit";
  isDrawerOpened?: boolean;
}

export function LaborVitalsFormFields({
  form,
  clientId,
  mode = "quick-add",
  isDrawerOpened = true,
}: LaborVitalsFormFieldsProps) {
  const medplum = useMedplum();
  const [temperatureUnitOptions, setTemperatureUnitOptions] = useState<ValueSetExpansionContains[]>(
    [],
  );
  const [temperatureMethodOptions, setTemperatureMethodOptions] = useState<
    ValueSetExpansionContains[]
  >([]);

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadValueSets = async () => {
      const [temperatureUnits, temperatureMethod] = await Promise.all([
        getValueSetOptions(medplum, QuiltedHealthValueSets.TemperatureUnits),
        getValueSetOptions(medplum, LOINCValueSets.TemperatureMethod),
      ]);
      if (isCancelled) {
        return;
      }
      setTemperatureUnitOptions(temperatureUnits ?? []);
      setTemperatureMethodOptions(temperatureMethod ?? []);
    };

    void loadValueSets();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  const unitSystem = getUnitSystemFromTemperatureUnitSelectValue(form.values.temperatureUnit);
  const temperatureUnit = getVitalsUnitFields(unitSystem).temperatureUnit;

  const handleUnitSystemChange = (nextUnitSystem: VitalsUnitSystem) => {
    if (nextUnitSystem === unitSystem) return;

    const rawTemperature = form.values.temperatureValue;
    const temperatureNumber =
      typeof rawTemperature === "number"
        ? rawTemperature
        : typeof rawTemperature === "string" && rawTemperature !== ""
          ? Number(rawTemperature)
          : undefined;

    const convertedTemperature = convertTemperatureValue(
      temperatureNumber != null && Number.isFinite(temperatureNumber)
        ? temperatureNumber
        : undefined,
      temperatureUnit,
      nextUnitSystem,
    );

    form.setFieldValue(
      "temperatureUnit",
      getTemperatureUnitSelectValue(nextUnitSystem, temperatureUnitOptions),
    );
    if (convertedTemperature !== undefined) {
      form.setFieldValue("temperatureValue", convertedTemperature);
    }
  };

  return (
    <VitalsFormStack mt={0}>
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName="vitalDate"
        timeFieldName="vitalTime"
        autoPopulateDateTime={mode === "quick-add"}
        dateTimeGroupLinkId={LABOR_VITALS_DATE_TIME_LINK_ID}
      />

      <VitalsUnitsToggle
        value={unitSystem}
        onChange={handleUnitSystemChange}
        testIdPrefix="labor-vitals-units"
      />

      <VitalsLabeledSection label="Blood Pressure">
        <VitalsPairedFields>
          <VitalsNumberInput
            aria-label="Systolic"
            data-path="bloodPressure.systolic"
            rightSection={<VitalsUnitRightSection unit="Systolic" />}
            {...form.getInputProps("systolic")}
            data-testid={questionnaireFieldTestId("bloodPressure.systolic")}
          />
          <VitalsNumberInput
            aria-label="Diastolic"
            data-path="bloodPressure.diastolic"
            rightSection={<VitalsUnitRightSection unit="Diastolic" />}
            {...form.getInputProps("diastolic")}
            data-testid={questionnaireFieldTestId("bloodPressure.diastolic")}
          />
        </VitalsPairedFields>
      </VitalsLabeledSection>

      <VitalsHeartRateInput
        data-path="pulse"
        label="Heart Rate"
        {...form.getInputProps("pulse")}
        data-testid={questionnaireFieldTestId("pulse")}
      />

      <VitalsNumberInput
        label="Temperature"
        data-path="temperature.temperatureValue"
        rightSection={<VitalsUnitRightSection unit={getTemperatureUnitSuffix(temperatureUnit)} />}
        {...form.getInputProps("temperatureValue")}
        data-testid={questionnaireFieldTestId("temperature.temperatureValue")}
      />

      <NativeSelect
        label="Method"
        data-path="method"
        data={valueSetContainsToNativeSelectOptions(temperatureMethodOptions)}
        {...form.getInputProps("temperatureMethod")}
        data-testid={questionnaireFieldTestId("method")}
      />

      <Textarea
        label="Notes"
        data-path="notes"
        {...form.getInputProps("notes")}
        data-testid={questionnaireFieldTestId("notes")}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="signatureInitials"
        autoPopulateSignature={mode === "quick-add"}
      />
    </VitalsFormStack>
  );
}
